import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'clients';

// Action Types
export const types = {
  GET_CLIENTS: createAsyncTypes(`${KEY}/GET_CLIENTS`),
  CREATE_CLIENT: createAsyncTypes(`${KEY}/CREATE_CLIENT`),
  UPDATE_CLIENT: createAsyncTypes(`${KEY}/UPDATE_CLIENT`),
  DELETE_CLIENT: createAsyncTypes(`${KEY}/DELETE_CLIENT`),
  INVALIDATE_CACHE: createAsyncTypes(`${KEY}/INVALIDATE_CACHE`),
  DELETE_TRANSACTIONS: createAsyncTypes(`${KEY}/DELETE_TRANSACTIONS`),
  SET_CLIENT_ID: `${KEY}/SET_CLIENT_ID`,
  RESET_ACTION: `${KEY}/RESET_ACTION`,
  RESET_ERROR: `${KEY}/RESET_ERROR`
};

export interface KioskMapInstance {
  name: string;
  currentExternalId: string;
  previousExternalId: string;
}

export interface SalesReportSettings {
  baseUrl?: string;
  clientId?: string;
  eventIds?: number[];
  currentEventStartDate?: string;
  previousEventStartDate?: string;
  kioskMap?: KioskMapInstance[];
}

export interface Client {
  id: string;
  name: string;
  configuration: {
    upperMonitor?: boolean;
    kioskDetails?: boolean;
    genreTab?: boolean;
    salesReportSettings?: SalesReportSettings;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ClientsState {
  readonly clients: Client[];
  readonly clientId: string;
  readonly action: {
    readonly pending: boolean;
    readonly success: boolean;
    readonly error: string | null;
  };
}

export interface CreateClientPayload {
  name: string;
  configuration?: Client['configuration'];
}

export interface DeleteTransactionsPayload {
  kioskId?: string;
  integrationId?: string;
  clientId?: string;
}

export interface GetClientsRequest {
  type: typeof types.GET_CLIENTS.REQUEST;
}

export interface GetClientsPending {
  type: typeof types.GET_CLIENTS.PENDING;
  payload: boolean;
}

export interface GetClientsSuccess {
  type: typeof types.GET_CLIENTS.SUCCESS;
  payload: Client[];
}

export interface GetClientsError {
  type: typeof types.GET_CLIENTS.ERROR;
  payload: string;
}

export interface CreateClientRequest {
  type: typeof types.CREATE_CLIENT.REQUEST;
  payload: CreateClientPayload;
}

export interface CreateClientPending {
  type: typeof types.CREATE_CLIENT.PENDING;
  payload: boolean;
}

export interface CreateClientSuccess {
  type: typeof types.CREATE_CLIENT.SUCCESS;
}

export interface CreateClientError {
  type: typeof types.CREATE_CLIENT.ERROR;
  payload: string;
}

export type UpdateClientPayload = Partial<Client> & { id: string };

export interface UpdateClientRequest {
  type: typeof types.UPDATE_CLIENT.REQUEST;
  payload: UpdateClientPayload;
}

export interface UpdateClientPending {
  type: typeof types.UPDATE_CLIENT.PENDING;
  payload: boolean;
}

export interface UpdateClientSuccess {
  type: typeof types.UPDATE_CLIENT.SUCCESS;
  payload: Client;
}

export interface UpdateClientError {
  type: typeof types.UPDATE_CLIENT.ERROR;
  payload: string;
}

export interface DeleteClientRequest {
  type: typeof types.DELETE_CLIENT.REQUEST;
  payload: { id: string };
}

export interface DeleteClientPending {
  type: typeof types.DELETE_CLIENT.PENDING;
  payload: boolean;
}

export interface DeleteClientSuccess {
  type: typeof types.DELETE_CLIENT.SUCCESS;
}

export interface DeleteClientError {
  type: typeof types.DELETE_CLIENT.ERROR;
  payload: string;
}

export interface InvalidateCacheRequest {
  type: typeof types.INVALIDATE_CACHE.REQUEST;
}

export interface InvalidateCachePending {
  type: typeof types.INVALIDATE_CACHE.PENDING;
  payload: boolean;
}

export interface InvalidateCacheSuccess {
  type: typeof types.INVALIDATE_CACHE.SUCCESS;
}

export interface InvalidateCacheError {
  type: typeof types.INVALIDATE_CACHE.ERROR;
  payload: string;
}

export interface DeleteTransactionsRequest {
  type: typeof types.DELETE_TRANSACTIONS.REQUEST;
  payload: DeleteTransactionsPayload;
}

export interface DeleteTransactionsPending {
  type: typeof types.DELETE_TRANSACTIONS.PENDING;
  payload: boolean;
}

export interface DeleteTransactionsError {
  type: typeof types.DELETE_TRANSACTIONS.ERROR;
  payload: string;
}

export interface SetClientId {
  type: typeof types.SET_CLIENT_ID;
  payload: string;
}

export interface ResetAction {
  type: typeof types.RESET_ACTION;
}

export interface ResetError {
  type: typeof types.RESET_ERROR;
}

export type ClientsActionTypes =
  | GetClientsRequest
  | GetClientsPending
  | GetClientsSuccess
  | GetClientsError
  | CreateClientRequest
  | CreateClientPending
  | CreateClientSuccess
  | CreateClientError
  | UpdateClientRequest
  | UpdateClientPending
  | UpdateClientSuccess
  | UpdateClientError
  | DeleteClientRequest
  | DeleteClientPending
  | DeleteClientSuccess
  | DeleteClientError
  | InvalidateCacheRequest
  | InvalidateCachePending
  | InvalidateCacheSuccess
  | InvalidateCacheError
  | DeleteTransactionsRequest
  | DeleteTransactionsPending
  | DeleteTransactionsError
  | SetClientId
  | ResetAction
  | ResetError;
